import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Edit3, Trash2, Loader2, AlertCircle, X,
  FileText, FileSpreadsheet, FileCheck, Calendar, Package, Wheat, Hash, Download
} from 'lucide-react';
import axios from '../axiosInstance';

/* ───────────── helpers ───────────── */
const formatCurrency = (val) => {
  if (val === null || val === undefined || val === '') return '—';
  return `₹${parseFloat(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (val) => {
  if (!val) return '—';
  return new Date(val).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/* ───────────── component ───────────── */
export default function SlaDetail() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [sla, setSla] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [undertakingLoading, setUndertakingLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);

  /* delete */
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  /* ── fetch ── */
  useEffect(() => {
    const fetchSla = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`sla/${id}`);
        setSla(res.data?.data || res.data);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load SLA entry');
      } finally {
        setLoading(false);
      }
    };
    fetchSla();
  }, [id]);

  /* ── delete handler ── */
  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`sla/${id}`);
      setSuccess('SLA entry deleted successfully.');
      setShowDelete(false);
      setTimeout(() => navigate('/incidental/sla'), 1200);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete SLA entry');
    } finally {
      setDeleting(false);
    }
  };

  /* ── undertaking PDF ── */
  const handleUndertaking = async () => {
    setUndertakingLoading(true);
    try {
      const res = await axios.get(`sla/${id}/undertaking/pdf`);
      const { document_url } = res.data;
      if (document_url) {
        window.open(document_url, '_blank');
      } else {
        setError('No document URL returned for undertaking PDF');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch undertaking PDF');
    } finally {
      setUndertakingLoading(false);
    }
  };

  /* ── table PDF ── */
  const handleTable = async () => {
    setTableLoading(true);
    try {
      const res = await axios.get(`sla/${id}/table/pdf`);
      const { document_url } = res.data;
      if (document_url) {
        window.open(document_url, '_blank');
      } else {
        setError('No document URL returned for table PDF');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch table PDF');
    } finally {
      setTableLoading(false);
    }
  };

  /* ── loading ── */
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 text-blue-600">
          <Loader2 size={40} className="animate-spin" />
          <span className="font-bold text-sm">Loading SLA details...</span>
        </div>
      </div>
    );
  }

  /* ── error state ── */
  if (!sla && error) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] p-4 md:p-8">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl text-sm font-semibold">
            <AlertCircle size={20} /> {error}
          </div>
          <button onClick={() => navigate('/incidental/sla')} className="mt-4 text-blue-600 font-bold text-sm hover:underline">
            ← Back to SLA List
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-4 md:p-8 text-slate-800 font-sans">
      <div className="max-w-3xl mx-auto">

        {/* ── Header ── */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4"
        >
          <div className="flex items-center gap-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/incidental/sla')}
              className="p-3 bg-white border border-slate-200 hover:border-blue-300 rounded-2xl shadow-sm transition-all"
            >
              <ArrowLeft size={20} className="text-slate-600" />
            </motion.button>
            <div>
              <h5 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">Incidental Management</h5>
              <h1 className="text-3xl md:text-4xl font-black text-blue-800 tracking-tight">SLA Details</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => navigate(`/incidental/sla/${id}/edit`)}
              className="flex items-center gap-2 px-5 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-2xl font-bold text-sm shadow-lg shadow-amber-100 transition-all"
            >
              <Edit3 size={16} /> Edit
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => setShowDelete(true)}
              className="flex items-center gap-2 px-5 py-3 bg-red-500 hover:bg-red-600 text-white rounded-2xl font-bold text-sm shadow-lg shadow-red-100 transition-all"
            >
              <Trash2 size={16} /> Delete
            </motion.button>
          </div>
        </motion.div>

        {/* ── Success ── */}
        <AnimatePresence>
          {success && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="mb-6 flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-2xl text-sm font-semibold">
              <FileCheck size={20} /> {success}
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Error ── */}
        <AnimatePresence>
          {error && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="mb-6 flex items-center gap-3 p-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl text-sm font-semibold">
              <AlertCircle size={20} /> {error}
              <button onClick={() => setError('')} className="ml-auto p-1 hover:bg-red-100 rounded-lg transition"><X size={16} /></button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Detail Card ── */}
        {sla && (
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="space-y-6"
          >
            {/* Main Info */}
            <div className="bg-white rounded-3xl shadow-xl border border-slate-100 p-6 md:p-8">
              <h2 className="text-lg font-black text-slate-800 mb-6 flex items-center gap-2">
                <Package size={20} className="text-blue-600" /> SLA Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1">
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                    <Calendar size={12} /> Season
                  </p>
                  <p className="text-lg font-bold text-slate-800">{sla.season?.name || '—'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                    <Wheat size={12} /> Commodity
                  </p>
                  <p className="text-lg font-bold text-slate-800">{sla.commodity?.commodityname || sla.commodity?.name || '—'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                    <Hash size={12} /> Bags
                  </p>
                  <p className="text-lg font-bold text-slate-800">{sla.bags ?? '—'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                    <Package size={12} /> Quantity (Qtl)
                  </p>
                  <p className="text-lg font-bold text-slate-800">{sla.quantity ? parseFloat(sla.quantity).toLocaleString('en-IN') : '—'}</p>
                </div>
              </div>
            </div>

            {/* MSP & Amount */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-3xl border border-indigo-100 p-6 shadow-lg">
                <p className="text-xs font-bold text-indigo-500 uppercase tracking-wider mb-2">MSP (₹/Qtl)</p>
                <p className="text-3xl font-black text-indigo-700">{formatCurrency(sla.msp)}</p>
              </div>
              <div className="bg-gradient-to-br from-emerald-50 to-green-50 rounded-3xl border border-emerald-100 p-6 shadow-lg">
                <p className="text-xs font-bold text-emerald-500 uppercase tracking-wider mb-2">Calculated Amount</p>
                <p className="text-3xl font-black text-emerald-700">{formatCurrency(sla.amount)}</p>
              </div>
            </div>

            {/* Documents (placeholders) */}
            <div className="bg-white rounded-3xl shadow-xl border border-slate-100 p-6 md:p-8">
              <h2 className="text-lg font-black text-slate-800 mb-6 flex items-center gap-2">
                <FileText size={20} className="text-blue-600" /> Documents
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-2xl border border-slate-200 hover:border-red-200 transition-colors">
                  <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                    <FileText size={24} className="text-red-500" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-slate-700">Undertaking</p>
                    <p className="text-xs text-slate-400">View undertaking document</p>
                  </div>
                  <button
                    onClick={handleUndertaking}
                    disabled={undertakingLoading}
                    className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-xl text-xs font-bold transition disabled:opacity-50 flex items-center gap-1.5"
                  >
                    {undertakingLoading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                    {undertakingLoading ? 'Loading...' : 'Download'}
                  </button>
                </div>
                <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-2xl border border-slate-200 hover:border-emerald-200 transition-colors">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <FileSpreadsheet size={24} className="text-emerald-500" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-slate-700">Table</p>
                    <p className="text-xs text-slate-400">Download table PDF document</p>
                  </div>
                  <button
                    onClick={handleTable}
                    disabled={tableLoading}
                    className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-xs font-bold transition disabled:opacity-50 flex items-center gap-1.5"
                  >
                    {tableLoading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                    {tableLoading ? 'Loading...' : 'Download'}
                  </button>
                </div>
              </div>
            </div>

            {/* Timestamps */}
            <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Created</span>
                  <p className="font-semibold text-slate-700 mt-1">{formatDate(sla.created_at || sla.createdAt)}</p>
                </div>
                <div>
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Last Updated</span>
                  <p className="font-semibold text-slate-700 mt-1">{formatDate(sla.updated_at || sla.updatedAt)}</p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* ── Delete Confirmation Modal ── */}
        <AnimatePresence>
          {showDelete && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
              onClick={() => !deleting && setShowDelete(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-3xl shadow-2xl p-8 max-w-sm w-full"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                    <Trash2 size={28} className="text-red-600" />
                  </div>
                  <h3 className="text-xl font-black text-slate-800 mb-2">Delete SLA Entry?</h3>
                  <p className="text-slate-500 text-sm mb-6">This action cannot be undone. The SLA record will be permanently removed.</p>
                  <div className="flex gap-3 w-full">
                    <button
                      onClick={() => setShowDelete(false)}
                      disabled={deleting}
                      className="flex-1 px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-2xl font-bold text-sm transition disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDelete}
                      disabled={deleting}
                      className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-2xl font-bold text-sm transition disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {deleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                      {deleting ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
